import { useState } from "react";
import { Form, Button, Row, Col, Alert, Spinner } from "react-bootstrap";
import ButtonArrow from "../components/ButtonArrow";
import "../assets/scss/components/custom-form.scss";
import { formatPrice, sanitizeString } from "../Functions";

function CustomForm({ type, onFormSubmit=null }) {
    const API = process.env.REACT_APP_API_BASE_URL;
    const formDefault = {
        name: "",
        email: "",
        phone: "",
        propertyType: "",
        landSize: null,
        bedrooms: null,
        location: "",
        budget: null,
        paymentStructure: "",
        message: "",
        personType: "",
    }
    const [formState, setFormState] = useState(formDefault);
    const [formFeedback, setFormFeedback] = useState({
        status: "",
        message: "",
    });
    const [isLoading, setIsLoading] = useState(null);

    // State to manage price entered in form
    const [displayBudget, setDisplayBudget] = useState(formatPrice(formState?.budget));

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    // Handle change to price field
    const handleBudgetChange = (e) => {
        const value = e.target.value;
        const sanitizedValue = sanitizeString(value);

        // Update the display with the formatted value
        setDisplayBudget((formatPrice(sanitizedValue)));

        // Update the state with the clean, numeric value
        setFormState((prevState) => ({
            ...prevState,
            price: sanitizedValue,
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Clear previous feedback
        setFormFeedback({
            status: "",
            message: "",
        });
        setIsLoading(true);

        const payload = {
            full_name: formState.name,
            email: formState.email,
            phone: formState.phone,
            location: formState.location,
            property_type: formState.propertyType,
            land_size: formState.landSize,
            bedrooms: formState.bedrooms,
            budget: formState.budget,
            person_type: formState.personType,
            message: formState.message,
        };

        /* Make API call */
        fetch(`${API}/inquiries/create/`, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            if (response.ok) {
                return response.json().then((data) => {
                    setFormFeedback({ /* Success */
                        status: "success",
                        message: "Form submitted successfully. We will reach out to you shortly.",
                    });
                    setFormState(formDefault);

                    // Call the onFormSubmit callback if provided (for BookInspection flow)
                    if (onFormSubmit) {
                        onFormSubmit(data.id, {
                            full_name: formState.name,
                            email: formState.email,
                            phone: formState.phone,
                        });
                    }
                });
            }
            else {
                setFormFeedback({ /* Fail */
                    status: "failed",
                    message: "Something went wrong, please try again.",
                });
            }
        })
        .catch((error) => {
            setFormFeedback({ /* Fail */
                status: "failed",
                message: "An error has occured.",
            });
            console.error(`Error in form submission: ${error.message}`);
        })
        .finally(() => {
            setIsLoading(false);
        })
    }

    return (
        <Form onSubmit={ handleSubmit } className={`inquiry-form ${type} px-4 ${ type === "full-inquiry" ? "pt-1" : "pt-5" } pb-3 border mx-auto`}>
            {/* Name field */}
            <Form.Group className="mb-4">
                <Form.Label htmlFor="name">Full Name{type === "full-inquiry" && <small className="required text-danger fst-italic ms-1">&#40;required&#41;</small>}</Form.Label>
                <Form.Control
                    id="name"
                    type="text"
                    name="name"
                    placeholder="Title [First name] [Last name]"
                    value={ formState.name }
                    onChange={ handleChange }
                    autoComplete="name"
                    required
                />
            </Form.Group>

            {/* Email and Phone field */}
            <Row xs={1} sm={1} md={2} lg={2} xl={2} xxl={2}>
                <Form.Group as={Col} className="mb-4">
                    <Form.Label htmlFor="email">Email{type === "full-inquiry" && <small className="required text-danger fst-italic ms-1">&#40;required&#41;</small>}</Form.Label>
                    <Form.Control
                        id="email"
                        type="email"
                        name="email"
                        placeholder="max@gmail.com"
                        value={ formState.email }
                        onChange={ handleChange }
                        autoComplete="email"
                        required
                    />
                </Form.Group>

                <Form.Group as={Col} className="mb-4">
                    <Form.Label htmlFor="phone">Phone Number{type === "full-inquiry" && <small className="required text-danger fst-italic ms-1">&#40;required&#41;</small>}</Form.Label>
                    <Form.Control
                        id="phone"
                        type="tel"
                        name="phone"
                        placeholder="+234**********"
                        value={ formState.phone }
                        onChange={ handleChange }
                        autoComplete="tel"
                        required
                    />
                </Form.Group>
            </Row>

            {/* Property type, Size field and Preferred location */}
            { type !== "contact" ?
                <Row xs={1} sm={1} md={2} lg={2} xl={2} xxl={2}>
                    {/* Property Type */}
                    <Form.Group as={Col} className="mb-4">
                        <Form.Label htmlFor="property-type">Property Type</Form.Label>
                        <Form.Select id="property-type" name="propertyType" className="px-3 py-2" value={ formState.propertyType} onChange={ handleChange }>
                            <option value="">Select</option>
                            <option value="Apartment">Apartment</option>
                            <option value="Terrace Duplex">Terrace Duplex</option>
                            <option value="Semi Detached Duplex">Semi-Detached Duplex</option>
                            <option value="Fully Detached Duplex">Fully Detached Duplex</option>
                            <option value="Bungalow">Bungalow</option>
                            <option value="Land">Land</option>
                            <option value="Office">Office space</option>
                        </Form.Select>
                    </Form.Group>

                    {formState.propertyType.includes("Land") ?
                        <Form.Group as={Col} className="mb-4">
                            <Form.Label htmlFor="landSize">Size</Form.Label>
                            <Form.Control
                                id="landSize"
                                type="number"
                                min={1}
                                name="landSize"
                                placeholder="Plot size"
                                value={ formState.landSize }
                                onChange={ handleChange }
                            />
                        </Form.Group>
                    :                        
                        <Form.Group as={Col} className="mb-4">
                            <Form.Label htmlFor="bedrooms">Bedrooms</Form.Label>
                            <Form.Control
                                id="bedrooms"
                                type="number"
                                min={1}
                                name="bedrooms"
                                placeholder="No. of bedrooms"
                                value={ formState.bedrooms }
                                onChange={ handleChange }
                            />
                        </Form.Group>
                        }
                </Row> :
                <></>
            }

            {/* Budget and Payment structure field */}
            { type !== "contact" &&
                <Row xs={1} sm={1} md={2} lg={2} xl={2} xxl={2}>
                    <Form.Group as={Col} className="mb-4">
                        <Form.Label htmlFor="budget">Max Price</Form.Label>
                        <Form.Control
                            id="budget"
                            type="text"
                            name="budget"
                            placeholder="₦*******"
                            value={ displayBudget }
                            onChange={ handleBudgetChange }
                        />
                    </Form.Group>

                    <Form.Group as={Col} className="mb-4">
                        <Form.Label htmlFor="payment-structure">Preferred Payment Structure</Form.Label>
                        <Form.Select id="payment-structure" name="paymentStructure" className="px-3" value={ formState.paymentStructure } onChange={ handleChange }>
                            <option>--Select--</option>
                            <option value="Outright">Outright</option>
                            <option value="Off plan">Off plan</option>
                            <option value="3">--3 months</option>
                            <option value="6">--6 months</option>
                            <option value="12">--12 months</option>
                        </Form.Select>
                    </Form.Group>
                </Row>
            }

            {/* Location & Client type field */}
            <Row xs={1} sm={1} md={2} lg={2} xl={2} xxl={2}>
                { type === "full-inquiry" &&
                    <Form.Group as={Col} className="mb-4">
                        <Form.Label htmlFor="location">Preferred Location</Form.Label>
                        <Form.Control
                            id="location"
                            type="text"
                            name="location"
                            placeholder="(e.g. Lekki Phase 1)"
                            value={ formState.location }
                            onChange={ handleChange }
                        />
                    </Form.Group>
                }
                { type !== "basic-inquiry" &&
                    <Form.Group as={Col} className="mb-4">
                        <Form.Label htmlFor="client-type">I am a ...</Form.Label>
                        <Form.Select id="client-type" name="personType" className="px-3" value={ formState.personType } onChange={ handleChange }>
                            <option>--Select--</option>
                            <option value="CLIENT">Buyer</option>
                            <option value="REALTOR">Realtor</option>
                            <option value="DEVELOPER">Developer</option>
                        </Form.Select>
                    </Form.Group>
                }
            </Row>

            {/* Message field */}
            { type !== "basic-inquiry" &&
                <Form.Group className="mb-4">
                    <Form.Label htmlFor="message">Message</Form.Label>
                    <Form.Control
                        id="message"
                        as="textarea" rows={4}
                        name="message"
                        placeholder="Texting..."
                        value={ formState.message }
                        onChange={ handleChange }
                    />
                </Form.Group>
            }

            <Button
                className={`d-flex align-items-center justify-content-center gap-3 w-100`} type="submit"
                disabled={isLoading}
            >
                {isLoading &&
                    <Spinner animation="border" variant="white" size="sm" />
                }
                {
                    type !== "basic-inquiry" ?
                        <>Submit Inquiry { !isLoading &&<ButtonArrow /> }</>
                    : "Submit"
                }
            </Button>

            {formFeedback.status != null &&
                <Alert variant={ formFeedback.status === "success" ? "primary" : "danger" } className={`${formFeedback.status} my-3 pt-3 notification`}>
                    { formFeedback.message }
                </Alert>
            }
        </Form>
    );
};

export default CustomForm;
